import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sparkles, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HUMAN = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());

const toneFor = (rate) => {
  if (rate === null) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (rate >= 0.9) return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (rate >= 0.7) return 'bg-blue-50 text-blue-800 border-blue-200';
  if (rate >= 0.5) return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-red-50 text-red-800 border-red-200';
};

/**
 * Per-field hit-rate tile for AI document extraction. Silent when no
 * confirmed extractions exist yet.
 */
const ExtractionAccuracyPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ai/extraction-accuracy`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || data.total_confirmed_extractions === 0) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="extraction-accuracy-empty">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-700" />
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>AI Extraction Accuracy</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 py-2">
            No confirmed extractions yet. Once you use "Extract from Document" a few times,
            per-field hit-rates will show up here so you can see which fields the AI gets right most often.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="extraction-accuracy-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-700" />
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>AI Extraction Accuracy</CardTitle>
          </div>
          <span className="text-[11px] text-slate-500">
            <TrendingUp className="h-3 w-3 inline mr-1" />
            {data.total_confirmed_extractions} confirmed extraction{data.total_confirmed_extractions === 1 ? '' : 's'}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {data.fields.map(f => {
            const pct = f.hit_rate === null ? '—' : `${Math.round(f.hit_rate * 100)}%`;
            return (
              <div key={f.field} className={`rounded-lg border p-2.5 ${toneFor(f.hit_rate)}`} data-testid={`accuracy-tile-${f.field}`}>
                <p className="text-[10px] font-medium uppercase tracking-wider opacity-80">{HUMAN(f.field)}</p>
                <p className="text-xl font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>{pct}</p>
                <p className="text-[10px] opacity-70">
                  {f.extracted_count === 0 ? 'no data' : `${f.extracted_count - f.corrected_count}/${f.extracted_count} correct`}
                </p>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[10px] text-slate-500">
          Hit-rate = share of extracted values you left unchanged during review. Lower numbers mean the AI is
          guessing that field wrong more often — worth teaching your team to double-check.
        </p>
      </CardContent>
    </Card>
  );
};

export default ExtractionAccuracyPanel;
